import * as React from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { Box, Grid, TextField, InputAdornment, useMediaQuery, useTheme } from "@mui/material";
import parse from 'html-react-parser';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Button from "@mui/material/Button";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { axiosRequestData } from "../../helpers/axios";
import { Lesson } from "../../interfaces/lesson";
import DialogHeader from "./dialogHeader";
import { NumericFormat } from "react-number-format";
import { DrillRecord } from "../../interfaces/drillRecord";
import DateIcon from "../icons/dateIcon";
import { drillScoreAddEvent } from '../mixPanelEvents/mixPanelEvents';
import DateIconResultRecordModal from '../icons/datePickerRecordResultIcon';

const CustomDialog = styled(Dialog)(({ theme }) => ({
    'font-family': 'ABC Repro, sans-serif !important',
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
    'margin': 'auto',
    '@media (min-width: 770px)': {
        'maxWidth': '680px',
        '& .MuiDialog-paper': {
            'margin': '0',
            'borderRadius': '8px',
        },
    },
}));

const ListBox = styled(Box)(({ theme }) => ({
    '& ul': {
        paddingLeft: "20px"
    },
    '& li': {
        marginBottom: "5px",
        fontSize: "16px",
        lineHeight: "122%"
    }
}));

const MobileModalFooter = styled(Box)(({ theme }) => ({
    '&.MuiBox-root::before': {
        content: '""',
        display: 'block',
        borderTop: "1px solid rgba(196, 196, 196, 0.53)",
        margin: "0 -16px",
        paddingBottom: "25px"
    }
}));

const MyDatePickerIcon = (props: any) => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V4C0 3.45 0.195833 2.97917 0.5875 2.5875C0.979167 2.19583 1.45 2 2 2H3V0H5V2H13V0H15V2H16C16.55 2 17.0208 2.19583 17.4125 2.5875C17.8042 2.97917 18 3.45 18 4V9H16V8H2V18H9V20H2ZM2 6H16V4H2V6ZM11 20V16.925L16.525 11.425C16.675 11.275 16.8417 11.1667 17.025 11.1C17.2083 11.0333 17.3917 11 17.575 11C17.775 11 17.9667 11.0375 18.15 11.1125C18.3333 11.1875 18.5 11.3 18.65 11.45L19.575 12.375C19.7083 12.525 19.8125 12.6917 19.8875 12.875C19.9625 13.0583 20 13.2417 20 13.425C20 13.6083 19.9667 13.7958 19.9 13.9875C19.8333 14.1792 19.725 14.35 19.575 14.5L14.075 20H11ZM12.5 18.5H13.45L16.475 15.45L16.025 14.975L15.55 14.525L12.5 17.55V18.5ZM16.025 14.975L15.55 14.525L16.475 15.45L16.025 14.975Z" fill="#1D1A1A"/>
    </svg>
);

const RecordResultsDialog = ({ show, setShow, lesson, record, callback, routeComing, updateDrillLessons }: {
    show: boolean,
    setShow: (value: boolean) => void,
    lesson: Lesson,
    record?: DrillRecord,
    callback?: (data: DrillRecord) => void,
    routeComing?: string,
    updateDrillLessons?: any
}
) => {
    const [dateValue, setDateValue] = useState<Dayjs | null>(dayjs(new Date()));
    const [inputValue, setInputValue] = useState("");
    const [inputValues, setInputValues] = useState(['', '', '', '', '']);
    const theme = useTheme();
    const isMobile = useMediaQuery('(max-width:769px)');
    const rangeMobileTabled = useMediaQuery('(min-width: 550px)');
    const onlyMobile = useMediaQuery('(max-width:550px)');
    const [isLoading, setIsLoading] = useState(false);
    const [recordID, setRecordID] = useState(0);

    const recordLabelDesc = lesson.drill.record_label || '';
    const isAverage = recordLabelDesc.startsWith('Average');
    const canBeNegative = lesson.name === "10 Ball 30";
    const [footerHeight, setFooterHeight] = useState(0);
    const dateInputRef = React.useRef<HTMLInputElement | null>(null);

    

    useEffect(() => {
        if (record) {
            setDateValue(dayjs(new Date(record.record_date)));
            if (isAverage) {
            //    TODO
            } else {
                setInputValue(record.score.toString());
            }
            setRecordID(record.record_id);
        }
    }, [record]);

    const handleClose = () => {
        document.body.style.touchAction = 'auto';
        document.body.style.position = 'unset';
        setShow(false);
    };

    const submit = () => {
        setIsLoading(true);
        let scoreValue:number;

        if (isAverage) {
            const numericValues = inputValues.map(val => parseFloat(val)).filter(val => !isNaN(val));
            const sum = numericValues.reduce((acc, curr) => acc + curr, 0);
            scoreValue = numericValues.length > 0 ? sum / numericValues.length : 0;
        } else {
            scoreValue = parseFloat(inputValue) || 0;
        }

        let payload: Record<string, any> = {
            date: dateValue?.format("YYYY-M-D"),
            score: scoreValue,
        };
        if (recordID) {
            payload = { ...payload, record_id: recordID };
        }

        axiosRequestData("POST", "/wp-json/pg/v2/lesson/" + lesson.ID + "/record", payload)
            .then(response => {
                if (callback)
                    callback({
                        record_id: recordID ? recordID : response.data.data.record_id,
                        record_date: dateValue ? dateValue.toString() : "",
                        score: scoreValue
                    });
            })
            .finally(() => {
                setIsLoading(false);
                setShow(false);
                setInputValue("");
                setInputValues(['', '', '', '', '']);
                setDateValue(dayjs(new Date()));
                if (updateDrillLessons) {
                    updateDrillLessons();
                }
            });
    };

    const handleValueChange = React.useCallback((index:any) => (values:any) => {
        setInputValues((prevValues) => {
          const newValues = [...prevValues];
          newValues[index] = values.value;
          return newValues;
        });
      }, []);

    useEffect(() => {
        if (show && isMobile) {
            // Apply styles to global header
            const header = document.querySelector('header');
            if (header) {
                (header as HTMLElement).style.zIndex = '999999';
                (header as HTMLElement).style.position = 'fixed';
                (header as HTMLElement).style.width = '100%';
                (header as HTMLElement).style.borderBottom = '2px solid #ebebeb';
            }

            // Apply styles to global footer
            const footer = document.querySelector('footer');
            if (footer) {
                (footer as HTMLElement).style.position = 'fixed';
                (footer as HTMLElement).style.zIndex = '9999';
                (footer as HTMLElement).style.width = '100%';
                (footer as HTMLElement).style.bottom = '0';
                (footer as HTMLElement).style.borderTop = "1px solid #DFD9D5";
                setFooterHeight((footer as HTMLElement).offsetHeight);
            }

            // Cleanup function to restore original styles
            return () => {
                if (header) {
                    (header as HTMLElement).style.zIndex = '';
                    (header as HTMLElement).style.position = '';
                    (header as HTMLElement).style.width = '';
                    (header as HTMLElement).style.borderBottom = '';
                }
                if (footer) {
                    (footer as HTMLElement).style.position = '';
                    (footer as HTMLElement).style.zIndex = '';
                    (footer as HTMLElement).style.width = '';
                    (footer as HTMLElement).style.bottom = '';
                    (footer as HTMLElement).style.borderTop = '';
                    setFooterHeight(0);
                }
            };
        }
    }, [show, isMobile]);

    return (
        <>
            <CustomDialog
                disableEscapeKeyDown={true}
                aria-labelledby="customized-dialog-title"
                open={show}
                onClose={handleClose}
                maxWidth="lg"
                fullScreen={isMobile}
            >
                {!isMobile && <DialogHeader title={"Play & Record Results"} closeAction={handleClose} recordResultDialog={true}></DialogHeader>}
                <DialogContent dividers 
                    sx={{ 
                        fontFamily: "'ABC Repro', sans-serif",
                        padding: rangeMobileTabled ? "40px !important" : "24px !important"
                        }}>
                    <Typography variant={"h1"}
                        sx={{
                            fontFamily: "'ABC Repro', sans-serif",
                            textAlign: "left",
                            fontSize: '36px',
                            fontWeight: 700,
                            lineHeight: "140%",
                            marginBottom: "8px",
                            marginTop: isMobile ? "33px" : "0px",
                        }}>
                        {parse(lesson.name)}
                    </Typography>
                    <Typography 
                        fontFamily="'ABC Repro', sans-serif"
                        fontSize={"16px"}
                        lineHeight={"122%"}
                        textAlign={"left"}
                        fontWeight={400}
                        marginBottom={"24px"}
                        >
                        {parse(lesson.drill.subheading)}
                    </Typography>
                    <Grid container>
                        <Grid item display={"flex"} style={{ width: '100%', marginBottom: '24px' }}>
                            <Box bgcolor={"#F4F2F0"}
                                padding={3}
                                borderRadius={1}
                                width={"100%"}>
                                <Typography
                                    sx={{
                                        fontFamily: "'ABC Repro', sans-serif",
                                        fontSize: "24px",
                                        lineHeight: "122%",
                                        fontWeight: 700,
                                        marginBottom: "15px"
                                    }}
                                >
                                    Game Rules
                                </Typography>
                                <ListBox>
                                    {lesson ? parse(lesson.drill.game_rules) : ''}
                                </ListBox>
                            </Box>
                        </Grid>
                        <Grid  item display={"flex"} style={{ width: '100%', marginBottom: '24px' }}>
                            <Box bgcolor={"#F4F2F0"}
                                width={"100%"}
                                padding={3}
                                borderRadius={1}>
                                <Typography sx={{
                                    fontFamily: "'ABC Repro', sans-serif",
                                    fontSize: "24px",
                                    lineHeight: "122%",
                                    fontWeight: 700,
                                    marginBottom: "15px"
                                }}>
                                    Scoring System
                                </Typography>
                                <ListBox>
                                    {lesson ? parse(lesson.drill.scoring_system) : ''}
                                </ListBox>
                            </Box>
                        </Grid>
                        <Grid item style={{ width: '100%' }}>
                            <Grid container bgcolor={"#F4F2F0"} sx={{ width: '100%', display: 'flex', flexDirection: 'column', padding: '25px' }}>
                                <Grid item sx={{ marginBottom: '15px' }}>
                                    <Typography 
                                        sx={{
                                        fontFamily: "'ABC Repro', sans-serif",
                                        fontSize: "24px",
                                        lineHeight: "122%",
                                        fontWeight: 700,
                                        marginBottom: "15px"
                                    }}>
                                        Record Results
                                    </Typography>
                                    <Typography sx={{
                                        fontFamily: "'ABC Repro', sans-serif",
                                        fontSize: "16px",
                                        lineHeight: "122%",
                                        fontWeight: 400,
                                        color: "#383838",
                                        marginBottom: "8px"
                                    }}>
                                        Date</Typography>
                                    <DatePicker
                                        value={dateValue}
                                        onChange={(newValue) => setDateValue(newValue)}
                                        disableOpenPicker
                                        slots={{ openPickerIcon: undefined }}
                                        slotProps={{
                                            textField: {
                                            required: true,
                                            size: "small",
                                            inputRef: dateInputRef,
                                            InputProps: {
                                                style: {
                                                height: "53px",
                                                },
                                                endAdornment: (
                                                <InputAdornment position="end">
                                                    <DateIconResultRecordModal
                                                    sx={{ cursor: "pointer" }}
                                                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();

                                                        const input =
                                                        dateInputRef.current ??
                                                        (e.currentTarget
                                                            .closest(".MuiOutlinedInput-root")
                                                            ?.querySelector("input") as HTMLInputElement | null);

                                                        if (input) {
                                                        input.focus();
                                                        input.click();
                                                        }
                                                    }}
                                                    />
                                                </InputAdornment>
                                                ),
                                            },
                                            },
                                        }}
                                        sx={{ width: "100%", backgroundColor: "#fff" }}
                                        />
                                </Grid>
                                <Grid item>
                                    {isAverage ? (
                                         <Box>
                                         <Typography sx={{
                                                    fontFamily: "'ABC Repro', sans-serif",
                                                    fontSize: onlyMobile ? "24px" : "16px",
                                                    lineHeight: "122%",
                                                    fontWeight: 700,
                                                }}>
                                             {lesson ? parse(lesson.drill.record_label) : ''}
                                         </Typography>
                                         <Typography sx={{
                                                    fontFamily: "'ABC Repro', sans-serif",
                                                    fontSize: "16px",
                                                    lineHeight: "122%",
                                                    fontWeight: 400,
                                                }} my={"10px"}>
                                             {lesson ? parse(lesson.drill.record_label_desc) : ''}
                                         </Typography>
                                         <Box display="flex" justifyContent="start" alignItems="center" flexWrap="nowrap" gap={'4px'}>
                                         {inputValues.map((value, index) => (
                                            <NumericFormat
                                                key={index}
                                                value={value}
                                                onValueChange={handleValueChange(index)}
                                                customInput={TextField}
                                                allowNegative={canBeNegative}
                                                decimalScale={0}
                                                valueIsNumericString
                                                variant="outlined"
                                                size={"small"}
                                                placeholder={(index + 1).toString()}
                                                fullWidth
                                                required
                                                style={{ backgroundColor: "#ffffff"}}
                                            />
                                            ))}
                                         </Box>
                                     </Box>
                                    ) : (
                                        <Box>
                                            <Typography 
                                                sx={{
                                                    fontFamily: "'ABC Repro', sans-serif",
                                                    fontSize: onlyMobile ? "24px" : "16px",
                                                    lineHeight: "122%",
                                                    fontWeight: 700,
                                                }}>
                                                {lesson ? parse(lesson.drill.record_label) : ''}
                                            </Typography>
                                            <Typography sx={{
                                                fontFamily: "'ABC Repro', sans-serif",
                                                fontSize: "16px",
                                                lineHeight: "122%",
                                                fontWeight: 400,
                                            }} my={"10px"}>
                                                {lesson ? parse(lesson.drill.record_label_desc) : ''}
                                            </Typography>
                                            <NumericFormat
                                                value={inputValue}
                                                onValueChange={(values) => setInputValue(values.value)}
                                                customInput={TextField}
                                                allowNegative={canBeNegative}
                                                decimalScale={0}
                                                valueIsNumericString
                                                variant="outlined"
                                                size={"small"}
                                                placeholder="Enter number here..."
                                                fullWidth
                                                required
                                                style={{ backgroundColor: "#ffffff" }}
                                            />
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                            <Grid item sx={{ paddingBottom: isMobile ? `${footerHeight}px` : "0" }}>
                                <Button
                                    onClick={() => { submit(); drillScoreAddEvent(lesson); }}
                                    disabled={!dateValue?.isValid() || (isAverage ? inputValues.some(val => val === '') : inputValue === '') || isLoading}
                                    sx={{
                                        fontFamily: "'ABC Repro', sans-serif",
                                        backgroundColor: "#FD3300",
                                        fontWeight: 700,
                                        fontSize: 16,
                                        lineHeight: "122%",
                                        marginTop: "24px",
                                        width: "100%",
                                        borderRadius: "24px",
                                        minHeight: "46px",
                                        textTransform: "capitalize",
                                        "&:hover": { backgroundColor: "#DB2C00" }
                                    }}
                                    variant="contained">Submit</Button>
                                    <Button
                                    onClick={handleClose}
                                    sx={{
                                        fontFamily: "'ABC Repro', sans-serif",
                                        backgroundColor: "unset",
                                        border: "1.5px solid #000000",
                                        color: "#000000",
                                        fontWeight: 700,
                                        fontSize: 16,
                                        lineHeight: "122%",
                                        marginTop: "16px;",
                                        width: "100%",
                                        borderRadius: "24px",
                                        minHeight: "46px",
                                        textTransform: "capitalize",
                                        "&:hover": { backgroundColor: "rgb(244, 242, 240)" }
                                    }}
                                    variant="contained">Cancel</Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>
            </CustomDialog>
        </>
    );
};

export default RecordResultsDialog;
